import { useEffect, useMemo, useRef, useState } from "react";
import { Button, EmptyState, Field, Picker, RecordList, Spinner } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useReplaceCredits, useUpdateRelease, releaseUpdateBody } from "../../../app/hooks/useRelease.js";
import {
  useWritersBank,
  useMusiciansBank,
  useAddWriter,
  useUpdateWriter,
  useAddMusician,
  type WriterBankDto,
} from "../../../app/hooks/useCreditBanks.js";
import type { components } from "@cntrl/api-client";
import { SectionRail } from "../sectionRail.js";
import styles from "../section.module.scss";
import own from "./DetailsSection.module.scss";

type CreditDto = components["schemas"]["CreditDto"];
type CreditInput = components["schemas"]["CreditInput"];
type ReleaseOverviewDto = components["schemas"]["ReleaseOverview"];

// Details (shot-14) — metadata, credits & splits. The Writers/Musicians/Production credits are ONE
// credits set edited locally and saved with a single replace (If-Match); writer split totals WARN ≠100% without
// blocking (server `splitWarning`). Names autocomplete from the band-shared writers/musicians BANKS; a brand-new
// name is also added to the bank so it autocompletes the next release. UPC + track ISRCs are the Codes block.
// A writer's Affiliation (PRO) and IPI/CAE # live on the BANK entry, not the credit — so editing them writes
// straight through (PATCH) and shows on every release that credits that writer; the split % is per-release and
// rides the credits draft like everything else here.

export function DetailsSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const replace = useReplaceCredits(releaseId);
  const updateRelease = useUpdateRelease(releaseId);
  const writersBank = useWritersBank();
  const musiciansBank = useMusiciansBank();
  const addWriter = useAddWriter();
  const updateWriter = useUpdateWriter();
  const addMusician = useAddMusician();

  const serverCredits = q.data?.data?.credits;
  const version = q.data?.version;
  const tracks = q.data?.data?.tracks ?? [];
  const overview = q.data?.data?.overview;

  // The two local drafts this section edits — the credits and the IDs & ownership fields — re-synced from the
  // server whenever the aggregate version advances. ONE Save writes both, so a save in progress must not let the
  // refetch after its first write reset the other draft: if the second write then failed, its edits would be gone.
  const [draft, setDraft] = useState<CreditInput[]>([]);
  const [ownership, setOwnership] = useState<OwnershipDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const syncedVersion = useRef<number | null>(null);
  useEffect(() => {
    if (saving) return;
    if (version !== undefined && version !== syncedVersion.current) {
      setDraft((serverCredits?.items ?? []).map(toInput));
      if (overview) setOwnership(ownershipDraftOf(overview));
      syncedVersion.current = version;
    }
  }, [version, serverCredits, overview, saving]);

  const writers = draft.filter((c) => c.creditType === "writer");
  const musicians = draft.filter((c) => c.creditType === "musician");
  const production = draft.filter((c) => c.creditType !== "writer" && c.creditType !== "musician");
  const splitTotal = writers.reduce((sum, w) => sum + (w.splitPct ?? 0), 0);
  const splitWarn = writers.length > 0 && Math.round(splitTotal) !== 100;
  const creditsDirty = version !== undefined && JSON.stringify(draft) !== JSON.stringify((serverCredits?.items ?? []).map(toInput));
  const ownershipDirty =
    version !== undefined && !!overview && !!ownership && JSON.stringify(ownership) !== JSON.stringify(ownershipDraftOf(overview));
  const dirty = creditsDirty || ownershipDirty;

  const writerBankById = useMemo(() => byId(writersBank.data, (w) => w.writerId), [writersBank.data]);
  const musicianBankById = useMemo(() => byId(musiciansBank.data, (m) => m.musicianId), [musiciansBank.data]);
  // An unlinked writer row resolves to its bank entry by NAME — the rule addWriterRow uses — so a row whose link
  // is still only in the unsaved draft (or an older row that never had one) shows the bank's PRO / IPI.
  const bankFor = (c: CreditInput) =>
    writerBankById.get(c.writerId ?? matchBank(writersBank.data, (w) => w.name, c.displayName ?? "", (w) => w.writerId) ?? "");
  // One in-flight link per writer name: leaving Affiliation then IPI on an unlinked row fires two edits back to
  // back, and without this the second races the first's bank create and its value is lost.
  const pendingLinks = useRef(new Map<string, Promise<string | undefined>>());

  if (q.isLoading || !overview) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading details…" />
      </div>
    );
  }

  function update(next: CreditInput[]) {
    setDraft(next);
  }

  /** Commit an inline Affiliation / IPI edit on an existing writer row. The value lives on the bank entry, so
   *  it PATCHes the bank. A credit with no bank link yet (an older or hand-entered row) is linked first — to
   *  the bank entry of the same name, or a new one — and the link rides the credits draft (the section's Save). */
  async function editWriterBank(target: CreditInput, field: "pro" | "ipiCae", value: string) {
    let writerId = target.writerId ?? undefined;
    const name = target.displayName ?? "";
    if (!writerId) {
      const matched = matchBank(writersBank.data, (w) => w.name, name, (w) => w.writerId);
      if (!matched && !value) return;
      writerId = matched ?? (await linkByName(name));
      if (!writerId) return;
      const linked = writerId;
      setDraft((d) => d.map((c) => (c.creditType === "writer" && !c.writerId && c.displayName === name ? { ...c, writerId: linked } : c)));
    }
    const current = bankFor({ ...target, writerId })?.[field] ?? "";
    if (value === current) return;
    await updateWriter.mutateAsync({ writerId, body: { [field]: value } });
  }

  /** Create the bank entry for an unlinked writer name ONCE, however many edits ask for it at the same time. */
  function linkByName(name: string): Promise<string | undefined> {
    const key = name.trim().toLowerCase();
    const inFlight = pendingLinks.current.get(key);
    if (inFlight) return inFlight;
    const created = addWriter
      .mutateAsync({ name })
      .then((w) => w?.writerId ?? undefined)
      .finally(() => pendingLinks.current.delete(key));
    pendingLinks.current.set(key, created);
    return created;
  }

  function setSplit(target: CreditInput, splitPct: number | null) {
    update(draft.map((c) => (c === target ? { ...c, splitPct } : c)));
  }
  /** Replace one credit group (production or musicians) with an edited copy, leaving the others where they are.
   *  Order inside a group is the array order; positions are written from it at save time. */
  function setGroup(inGroup: (c: CreditInput) => boolean, next: CreditInput[]) {
    update([...draft.filter((c) => !inGroup(c)), ...next]);
  }
  const isWriter = (c: CreditInput) => c.creditType === "writer";
  const isProduction = (c: CreditInput) => c.creditType !== "writer" && c.creditType !== "musician";
  const isMusician = (c: CreditInput) => c.creditType === "musician";
  const moveIn = (group: CreditInput[], inGroup: (c: CreditInput) => boolean) => (from: number, to: number) => {
    const next = [...group];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    setGroup(inGroup, next);
  };
  const patchIn = (group: CreditInput[], inGroup: (c: CreditInput) => boolean, i: number, changes: Partial<CreditInput>) =>
    setGroup(inGroup, group.map((c, idx) => (idx === i ? { ...c, ...changes } : c)));

  /** A writer with no bank link (typed in a row) is linked to the bank entry of the same name, or banked now —
   *  reusing the one in-flight link per name an Affiliation / IPI edit may already have started. */
  async function linkWriters(credits: CreditInput[]): Promise<CreditInput[]> {
    const out: CreditInput[] = [];
    for (const c of credits) {
      if (c.creditType !== "writer" || c.writerId || !c.displayName) {
        out.push(c);
        continue;
      }
      const writerId = matchBank(writersBank.data, (w) => w.name, c.displayName, (w) => w.writerId) ?? (await linkByName(c.displayName));
      out.push({ ...c, writerId: writerId ?? null });
    }
    return out;
  }

  /** A musician with no bank link (typed in a row) is linked to the bank entry of the same name, or banked now, so
   *  the name autocompletes on the next release — what adding a musician used to do at add time. */
  async function linkMusicians(credits: CreditInput[]): Promise<CreditInput[]> {
    const out: CreditInput[] = [];
    for (const c of credits) {
      if (c.creditType !== "musician" || c.musicianId || !c.displayName) {
        out.push(c);
        continue;
      }
      let musicianId = matchBank(musiciansBank.data, (m) => m.name, c.displayName, (m) => m.musicianId);
      if (!musicianId) {
        const created = await addMusician.mutateAsync({ name: c.displayName, instruments: c.instruments ?? [] });
        musicianId = created?.musicianId ?? undefined;
      }
      out.push({ ...c, musicianId: musicianId ?? null });
    }
    return out;
  }

  /** One Save for the whole section: the IDs & ownership fields (the release PUT) and the credits set (the credits
   *  PUT). Both are guarded by the release's version, so they run in turn and the second sends the version the first
   *  returned. Each hook toasts its own failure; a failure stops the save with the unsaved draft still on screen. */
  async function save() {
    if (version === undefined || !overview) return;
    setSaving(true);
    try {
      let v = version;
      if (ownershipDirty && ownership) {
        const trim = (x: string) => (x.trim() ? x.trim() : null);
        const saved = await updateRelease.mutateAsync({
          version: v,
          body: releaseUpdateBody(overview, {
            coArtist: trim(ownership.coArtist),
            labelName: trim(ownership.labelName),
            copyright: trim(ownership.copyright),
            publisher: trim(ownership.publisher),
            pro: trim(ownership.pro),
            isrc: trim(ownership.isrc),
          }),
        });
        v = saved.version ?? v;
      }
      if (creditsDirty) {
        // A row added and left blank is dropped rather than sent; positions follow the on-screen order.
        const filled = draft.filter((c) => (c.displayName ?? "").trim());
        const linked = await linkWriters(await linkMusicians(filled.map((c) => ({ ...c, displayName: (c.displayName ?? "").trim() }))));
        await replace.mutateAsync({ version: v, body: { credits: linked.map((c, i) => ({ ...c, position: i })) } });
      }
    } catch {
      // Already toasted by the failing hook.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.section}>
      <SectionRail>
        {canWrite ? (
          <Button size="sm" onClick={() => void save()} disabled={!dirty} loading={saving}>
            Save
          </Button>
        ) : null}
      </SectionRail>

      <div className={styles.grid2}>
        <OwnershipCard
          overview={overview}
          draft={ownership ?? ownershipDraftOf(overview)}
          canWrite={canWrite}
          onChange={(k, v) => setOwnership((d) => ({ ...(d ?? ownershipDraftOf(overview)), [k]: v }))}
        />
        {/* Production credits sit BESIDE IDs & ownership at the top (Ryan's ART-201 layout), as a compact
            kv-grid with its own Add credit action. */}
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Production credits</h3>
          {canWrite ? (
            <RecordList
              draggable={false}
              borderStyle="lite"
              dragKind="release-production-credit"
              items={production}
              addLabel="Add credit"
              empty={<span className={own.emptyLine}>No production credits yet</span>}
              nameOf={(c, i) => c.displayName?.trim() || `credit ${i + 1}`}
              onAdd={() => setGroup(isProduction, [...production, { creditType: "producer", role: null, displayName: "" }])}
              onMove={moveIn(production, isProduction)}
              onRemove={(i) => setGroup(isProduction, production.filter((_, idx) => idx !== i))}
              renderFields={(c, i) => (
                <ProductionFields credit={c} index={i} onChange={(changes) => patchIn(production, isProduction, i, changes)} />
              )}
            />
          ) : production.length === 0 ? (
            <EmptyState title="No production credits yet" description="Add producer, engineer, mixed/mastered by, studio." />
          ) : (
            <div>
              {production.map((c, i) => (
                <Kv key={i} label={productionRoleOf(c)} value={c.displayName || "—"} empty={!c.displayName} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Writers + splits */}
      <div className={styles.card}>
        <div className={own.tableHead}>
          <h3 className={styles.cardTitle}>Writers</h3>
          <span className={own.splitTotal} data-warn={splitWarn || undefined}>
            Split total: {Math.round(splitTotal)}%{splitWarn ? " — should be 100%" : ""}
          </span>
        </div>
        {canWrite ? (
          <>
            {/* The same list as Production credits and Musicians, with the one TABLE header in the product. */}
            <RecordList
              draggable={false}
              borderStyle="lite"
              dragKind="release-writer-credit"
              items={writers}
              columns={WRITER_COLUMNS}
              addLabel="Add writer"
              empty={<span className={own.emptyLine}>No writers yet</span>}
              nameOf={(w, i) => w.displayName?.trim() || `writer ${i + 1}`}
              onAdd={() => setGroup(isWriter, [...writers, { creditType: "writer", displayName: "", splitPct: null }])}
              onMove={moveIn(writers, isWriter)}
              onRemove={(i) => setGroup(isWriter, writers.filter((_, idx) => idx !== i))}
              renderFields={(w, i) => (
                <WriterFields
                  credit={w}
                  index={i}
                  bank={bankFor(w)}
                  onName={(name) => patchIn(writers, isWriter, i, { displayName: name, writerId: null })}
                  onBankEdit={(field, value) => editWriterBank(w, field, value)}
                  onSplit={(pct) => setSplit(w, pct)}
                />
              )}
            />
            <datalist id="writers-bank">
              {(writersBank.data ?? []).map((w) => w.name).filter(Boolean).map((n) => (
                <option key={n!} value={n!} />
              ))}
            </datalist>
          </>
        ) : writers.length === 0 ? (
          <EmptyState title="No writers yet" description="Add writers with their splits (they warn, never block, at ≠100%)." />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Affiliation</th>
                <th>IPI / CAE #</th>
                <th className={styles.numeric}>Split %</th>
              </tr>
            </thead>
            <tbody>
              {writers.map((w, i) => {
                const bank = bankFor(w);
                return (
                  <tr key={i}>
                    <td className={own.name}>{w.displayName || bank?.name || "—"}</td>
                    <td>{bank?.pro || "—"}</td>
                    <td className={own.mono}>{bank?.ipiCae || "—"}</td>
                    <td className={styles.numeric}>{w.splitPct ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Musicians — the same add / edit in place / reorder / remove list as Production credits. */}
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Musicians</h3>
        {canWrite ? (
          <>
            <RecordList
              draggable={false}
              borderStyle="lite"
              dragKind="release-musician-credit"
              items={musicians}
              addLabel="Add musician"
              empty={<span className={own.emptyLine}>No musicians yet</span>}
              nameOf={(m, i) => m.displayName?.trim() || `musician ${i + 1}`}
              onAdd={() => setGroup(isMusician, [...musicians, { creditType: "musician", displayName: "", instruments: [] }])}
              onMove={moveIn(musicians, isMusician)}
              onRemove={(i) => setGroup(isMusician, musicians.filter((_, idx) => idx !== i))}
              renderFields={(m, i) => (
                <MusicianFields
                  credit={m}
                  index={i}
                  bankInstruments={m.musicianId ? musicianBankById.get(m.musicianId)?.instruments ?? null : null}
                  onChange={(changes) => patchIn(musicians, isMusician, i, changes)}
                />
              )}
            />
            <datalist id="musicians-bank">
              {(musiciansBank.data ?? []).map((m) => m.name).filter(Boolean).map((n) => (
                <option key={n!} value={n!} />
              ))}
            </datalist>
          </>
        ) : musicians.length === 0 ? (
          <EmptyState title="No musicians yet" description="Add performers and the instruments they played." />
        ) : (
          <div>
            {musicians.map((m, i) => {
              const bank = m.musicianId ? musicianBankById.get(m.musicianId) : undefined;
              const instruments = (m.instruments && m.instruments.length ? m.instruments : bank?.instruments ?? []).join(", ");
              return <Kv key={i} label={m.displayName || bank?.name || "—"} value={instruments || "—"} empty={!instruments} />;
            })}
          </div>
        )}
      </div>

      {/* Codes & registration — release-level identifiers (UPC + the Spotify/smart-link ids, read-only here as
          they're edited on section 01 Overview) plus the per-track ISRC table, full-width below the credits. The
          RELEASE-level ISRC lives in the ownership card above; these are the per-track ones. */}
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Codes & registration</h3>
        <div className={own.releaseCodes}>
          <Kv label="UPC" value={overview.upc || "Not set"} empty={!overview.upc} />
          {/* READ-ONLY here: these are ids, EDITED on section 01 (the sole editor). "Spotify album ID" and the
              overview field were one stored value under two labels; the public, fan-facing Spotify URL is a
              different row (the smart link's Spotify button, section 04). */}
          <Kv label="Spotify album ID" value={overview.spotifyAlbumId || "Not linked"} empty={!overview.spotifyAlbumId} />
          <Kv label="Smart-link slug" value={overview.smartLinkSlug || "Not set"} empty={!overview.smartLinkSlug} />
        </div>
        {tracks.length === 0 ? (
          <EmptyState title="No track codes yet" description="Track ISRCs show here as tracks are added." />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Track</th>
                <th>ISRC</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((t) => (
                <tr key={t.trackId}>
                  <td>{t.title || `Track ${t.position ?? ""}`}</td>
                  <td className={own.mono}>{t.isrc || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---- IDs & ownership -----------------------------------------------------------------------------------

/** The release-level "IDs & ownership" metadata (ART-201): Artist (read-only band name) + the editable
 *  Co-artist / Label / Copyright / Publisher / PRO / ISRC fields. The draft lives in the section and saves with the
 *  section's one Save, through the whole-record release PUT (`releaseUpdateBody` round-trips every field, so this
 *  never wipes another section's). */
type OwnershipDraft = { coArtist: string; labelName: string; copyright: string; publisher: string; pro: string; isrc: string };

function ownershipDraftOf(o: ReleaseOverviewDto): OwnershipDraft {
  return {
    coArtist: o.coArtist ?? "",
    labelName: o.labelName ?? "",
    copyright: o.copyright ?? "",
    publisher: o.publisher ?? "",
    pro: o.pro ?? "",
    isrc: o.isrc ?? "",
  };
}

function OwnershipCard({
  overview,
  draft,
  canWrite,
  onChange,
}: {
  overview: ReleaseOverviewDto;
  draft: OwnershipDraft;
  canWrite: boolean;
  onChange: (key: keyof OwnershipDraft, value: string) => void;
}) {
  const set = (k: keyof OwnershipDraft) => (v: string) => onChange(k, v);

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>IDs & ownership</h3>
      <div data-field-style="underline">
        {/* Artist is the band's own name — read-only here (it is the account identity, not a per-release field). */}
        <Kv label="Artist" value={overview.bandName || "—"} empty={!overview.bandName} />
        <EditKv label="Co-artist" value={draft.coArtist} placeholder="Featured / co-billed artist" canWrite={canWrite} onChange={set("coArtist")} />
        <EditKv label="Label" value={draft.labelName} placeholder="Self-released" canWrite={canWrite} onChange={set("labelName")} />
        <EditKv label="Copyright" value={draft.copyright} placeholder="© / ℗ line" canWrite={canWrite} onChange={set("copyright")} />
        <EditKv label="Publisher" value={draft.publisher} placeholder="Self / unsigned" canWrite={canWrite} onChange={set("publisher")} />
        <EditKv label="PRO" value={draft.pro} placeholder="BMI / ASCAP / SESAC" canWrite={canWrite} onChange={set("pro")} />
        <EditKv label="ISRC" value={draft.isrc} placeholder="XX-XXX-YY-NNNNN" mono canWrite={canWrite} onChange={set("isrc")} />
      </div>
    </div>
  );
}

/** One editable "IDs & ownership" row — a kv row whose value is the shared Field, in the card's underline style
 *  (no box; a bottom rule on hover/focus), right-aligned to read as the value. Falls back to a read-only {@link Kv} for a viewer without write. */
function EditKv({
  label,
  value,
  placeholder,
  mono,
  canWrite,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  mono?: boolean;
  canWrite: boolean;
  onChange: (v: string) => void;
}) {
  if (!canWrite) return <Kv label={label} value={value || placeholder || "—"} empty={!value} />;
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvKey}>{label}</span>
      <Field
        label={label}
        hideLabel
        className={`${own.ownField} ${mono ? own.mono : ""}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// ---- writers table row ---------------------------------------------------------------------------------

/** One writer row. For a writer seat it is editable in place: Affiliation and IPI/CAE # commit to the BANK on
 *  blur (shared across releases), Split % edits the credits draft as you type (saved with the section's Save). A
 *  reader gets the plain values. */
const WRITER_COLUMNS = [
  { label: "Name", width: "minmax(0, 1.6fr)" },
  { label: "Affiliation", width: "minmax(0, 1fr)" },
  { label: "IPI / CAE #", width: "minmax(0, 1fr)" },
  { label: "Split %", width: "88px", align: "end" as const },
];

/** One writer's fields (a row of the writers list). The name and Split % edit the credits draft (saved with the
 *  section's Save); Affiliation and IPI/CAE # live on the band's writers BANK and commit there on blur. A name that
 *  matches a bank writer shows that writer's Affiliation / IPI. */
function WriterFields({
  credit,
  index,
  bank,
  onName,
  onBankEdit,
  onSplit,
}: {
  credit: CreditInput;
  index: number;
  bank: WriterBankDto | undefined;
  onName: (name: string) => void;
  onBankEdit: (field: "pro" | "ipiCae", value: string) => Promise<void>;
  onSplit: (pct: number | null) => void;
}) {
  const name = credit.displayName || bank?.name || `writer ${index + 1}`;
  const [pro, setPro] = useState(bank?.pro ?? "");
  const [ipi, setIpi] = useState(bank?.ipiCae ?? "");
  const [split, setSplitText] = useState(credit.splitPct == null ? "" : String(credit.splitPct));
  // Re-seed from the bank when it changes underneath (a refetch after this or another row's edit).
  // Keyed on the NORMALISED value: a bank entry arriving for a row that had none flips the raw field from
  // undefined to null, which would otherwise re-seed — and wipe — whatever is being typed in the other box.
  const bankPro = bank?.pro ?? "";
  const bankIpi = bank?.ipiCae ?? "";
  useEffect(() => setPro(bankPro), [bankPro]);
  useEffect(() => setIpi(bankIpi), [bankIpi]);
  // …and the split from the draft when it moves for any reason other than this box (a server re-sync).
  useEffect(() => {
    setSplitText((text) => (parseSplit(text) === (credit.splitPct ?? null) ? text : credit.splitPct == null ? "" : String(credit.splitPct)));
  }, [credit.splitPct]);

  const commit = (field: "pro" | "ipiCae", raw: string) => {
    void onBankEdit(field, raw.trim()).catch(() => {
      // The mutation already toasted; put the box back to what the bank holds.
      if (field === "pro") setPro(bank?.pro ?? "");
      else setIpi(bank?.ipiCae ?? "");
    });
  };

  return (
    <>
      <Field
        label={`Writer ${index + 1} name`}
        hideLabel
        density="compact"
        placeholder="Writer name"
        list="writers-bank"
        className={own.suggestField}
        value={credit.displayName ?? ""}
        onChange={(e) => onName(e.target.value)}
      />
      <Field
        label={`Affiliation for ${name}`}
        hideLabel
        density="compact"
        placeholder="PRO"
        value={pro}
        onChange={(e) => setPro(e.target.value)}
        onBlur={() => commit("pro", pro)}
      />
      <Field
        label={`IPI / CAE # for ${name}`}
        hideLabel
        density="compact"
        className={own.monoField}
        placeholder="IPI / CAE #"
        value={ipi}
        onChange={(e) => setIpi(e.target.value)}
        onBlur={() => commit("ipiCae", ipi)}
      />
      <Field
        label={`Split % for ${name}`}
        hideLabel
        density="compact"
        className={own.splitField}
        inputMode="decimal"
        placeholder="0"
        value={split}
        onChange={(e) => {
          setSplitText(e.target.value);
          onSplit(parseSplit(e.target.value));
        }}
      />
    </>
  );
}

function parseSplit(text: string): number | null {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

const PRODUCTION_ROLES = ["Producer", "Engineer", "Mixed by", "Mastered by", "Studio"];

/** The role a production credit shows: a producer/engineer TYPE reads as its word, anything else as its role. */
function productionRoleOf(c: CreditInput): string {
  return c.role || creditTypeLabel(c.creditType);
}

/** A picked role writes the TYPE for the two the server knows by type, and `other` + the words for the rest — the
 *  shapes the add row always wrote, so stored credits keep reading the same on the public page. */
function roleChange(role: string): Partial<CreditInput> {
  if (role === "Producer") return { creditType: "producer", role: null };
  if (role === "Engineer") return { creditType: "engineer", role: null };
  return { creditType: "other", role };
}

function ProductionFields({ credit, index, onChange }: { credit: CreditInput; index: number; onChange: (c: Partial<CreditInput>) => void }) {
  const role = productionRoleOf(credit);
  // A stored role outside the list (an imported "Mix Engineer") stays selectable rather than silently changing.
  const roles = PRODUCTION_ROLES.includes(role) ? PRODUCTION_ROLES : [role, ...PRODUCTION_ROLES];
  return (
    <>
      <Picker
        label={`Credit ${index + 1} role`}
        hideLabel
        value={role}
        onChange={(r) => onChange(roleChange(r))}
        options={roles.map((r) => ({ value: r, label: r }))}
      />
      <Field
        label={`Credit ${index + 1} name`}
        hideLabel
        density="compact"
        placeholder="Name"
        value={credit.displayName ?? ""}
        onChange={(e) => onChange({ displayName: e.target.value })}
      />
    </>
  );
}

function MusicianFields({
  credit,
  index,
  bankInstruments,
  onChange,
}: {
  credit: CreditInput;
  index: number;
  bankInstruments: string[] | null;
  onChange: (c: Partial<CreditInput>) => void;
}) {
  const stored = credit.instruments && credit.instruments.length ? credit.instruments : bankInstruments ?? [];
  // The box keeps what is TYPED ("Drums, ") — re-joining the parsed list on every keystroke would eat the comma.
  const [text, setText] = useState(stored.join(", "));
  const parse = (t: string) => t.split(",").map((x) => x.trim()).filter(Boolean);
  useEffect(() => {
    setText((t) => (parse(t).join("|") === stored.join("|") ? t : stored.join(", ")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored.join("|")]);
  return (
    <>
      <Field
        label={`Musician ${index + 1} name`}
        hideLabel
        density="compact"
        placeholder="Musician name"
        list="musicians-bank"
        className={own.suggestField}
        value={credit.displayName ?? ""}
        onChange={(e) => onChange({ displayName: e.target.value, musicianId: null })}
      />
      <Field
        label={`Musician ${index + 1} instruments`}
        hideLabel
        density="compact"
        placeholder="Instruments (comma-separated)"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange({ instruments: parse(e.target.value) });
        }}
      />
    </>
  );
}

function Kv({ label, value, empty }: { label: string; value: string; empty?: boolean }) {
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvKey}>{label}</span>
      <span className={styles.kvVal} data-empty={empty || undefined}>
        {value}
      </span>
    </div>
  );
}

// ---- helpers -------------------------------------------------------------------------------------------

function toInput(c: CreditDto): CreditInput {
  return {
    trackId: c.trackId ?? null,
    creditType: c.creditType ?? "other",
    writerId: c.writerId ?? null,
    musicianId: c.musicianId ?? null,
    displayName: c.displayName ?? "",
    role: c.role ?? null,
    splitPct: c.splitPct ?? null,
    instruments: c.instruments ?? null,
    position: c.position ?? null,
  };
}

function byId<T>(items: T[] | undefined, id: (t: T) => string | undefined): Map<string, T> {
  const m = new Map<string, T>();
  for (const it of items ?? []) {
    const key = id(it);
    if (key) m.set(key, it);
  }
  return m;
}

function matchBank<T>(items: T[] | undefined, name: (t: T) => string | null | undefined, target: string, id: (t: T) => string | undefined): string | undefined {
  const found = (items ?? []).find((it) => (name(it) ?? "").toLowerCase() === target.toLowerCase());
  return found ? id(found) : undefined;
}

function creditTypeLabel(t: string | null | undefined): string {
  switch (t) {
    case "producer":
      return "Producer";
    case "engineer":
      return "Engineer";
    default:
      return "Credit";
  }
}
